/**
 * Video Content Slide
 * Displays YouTube, Vimeo, or custom video content.
 * Automatically converts watch/share URLs to embeddable formats.
 */

import { useState } from 'react';
import type { ContentSlideProps } from '../types';
import { stripHtml } from '../../../../utils/slides/stripHtml';

/**
 * Convert a YouTube URL (watch, short, share, already-embed) into an embed URL.
 * Returns null if the URL is not recognised as YouTube.
 */
function toYouTubeEmbed(raw: string): string | null {
  try {
    const url = new URL(raw);
    let videoId: string | null = null;

    // https://www.youtube.com/watch?v=VIDEO_ID
    if (
      (url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com' || url.hostname === 'm.youtube.com') &&
      url.pathname === '/watch'
    ) {
      videoId = url.searchParams.get('v');
    }

    // https://youtu.be/VIDEO_ID
    if (url.hostname === 'youtu.be') {
      videoId = url.pathname.slice(1).split('/')[0] || null;
    }

    // https://www.youtube.com/shorts/VIDEO_ID
    if (
      (url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com') &&
      url.pathname.startsWith('/shorts/')
    ) {
      videoId = url.pathname.replace('/shorts/', '').split('/')[0] || null;
    }

    // Already an embed URL — return as-is
    if (
      (url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com') &&
      url.pathname.startsWith('/embed/')
    ) {
      return raw;
    }

    if (videoId) {
      const start = url.searchParams.get('t') || url.searchParams.get('start');
      const params = new URLSearchParams({ rel: '0' });
      if (start) params.set('start', start.replace('s', ''));
      return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    }
  } catch {
    // not a valid URL
  }
  return null;
}

/**
 * Convert a Vimeo URL into an embed URL.
 */
function toVimeoEmbed(raw: string): string | null {
  try {
    const url = new URL(raw);

    // Already embed
    if (url.hostname === 'player.vimeo.com') return raw;

    if (url.hostname === 'vimeo.com' || url.hostname === 'www.vimeo.com') {
      const match = url.pathname.match(/^\/(\d+)/);
      if (match) return `https://player.vimeo.com/video/${match[1]}`;
    }
  } catch {
    // not a valid URL
  }
  return null;
}

/**
 * Determine the best embeddable URL for the given video.
 */
function getEmbedUrl(videoUrl: string, provider?: string): string {
  if (provider === 'vimeo' || (!provider && /vimeo/i.test(videoUrl))) {
    return toVimeoEmbed(videoUrl) || videoUrl;
  }

  // default to youtube
  return toYouTubeEmbed(videoUrl) || videoUrl;
}

/** Whether the provider uses an iframe embed */
function isEmbedProvider(provider?: string): boolean {
  return !provider || provider === 'youtube' || provider === 'vimeo';
}

export default function VideoSlide({ slide, className = '' }: ContentSlideProps) {
  const { content } = slide;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const embedUrl = content?.videoUrl
    ? getEmbedUrl(content.videoUrl, content.provider)
    : '';

  return (
    <div className={`mb-8 max-w-3xl mx-auto ${className}`}>
      {content?.showTitle !== false && content?.title && (
        <h2 className="text-3xl font-bold mb-4 text-center">
          {stripHtml(content.title)}
        </h2>
      )}

      {content?.videoUrl && isEmbedProvider(content.provider) && !error && (
        <div className="relative w-full bg-black rounded-2xl overflow-hidden shadow-lg mb-4 aspect-video max-h-[70vh]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-muted">
              <div className="flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Loading video…</p>
              </div>
            </div>
          )}
          <iframe
            src={embedUrl}
            title="Video content"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            onLoad={() => setLoading(false)}
            onError={() => { setLoading(false); setError(true); }}
            className="absolute inset-0 w-full h-full"
          />
        </div>
      )}

      {content?.videoUrl && !isEmbedProvider(content.provider) && (
        <div className="relative w-full bg-black rounded-2xl overflow-hidden shadow-lg mb-4 aspect-video max-h-[70vh]">
          <video
            src={content.videoUrl}
            controls
            playsInline
            autoPlay={Boolean(content.autoplay)}
            className="absolute inset-0 w-full h-full"
            onError={() => setError(true)}
          />
        </div>
      )}

      {/* Fallback when embed is blocked */}
      {content?.videoUrl && error && (
        <div className="w-full aspect-video max-h-[70vh] rounded-2xl border-2 border-border shadow-lg bg-muted flex flex-col items-center justify-center gap-4 px-6 text-center mb-4">
          <div className="text-4xl">🎬</div>
          <p className="text-lg font-semibold text-foreground">Video could not be loaded</p>
          <p className="text-sm text-muted-foreground">
            The video provider may have blocked embedding. You can watch it directly instead.
          </p>
          <a
            href={content.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Open video in new tab ↗
          </a>
        </div>
      )}

      {content?.videoUrl && (
        <div className="text-right">
          <a
            href={content.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          >
            Open in new tab ↗
          </a>
        </div>
      )}

      {content?.description && (
        <p className="text-muted-foreground mt-2">{content.description}</p>
      )}
    </div>
  );
}
